import React from 'react';
import { Button } from './ui/button';
import { hardReloadApp } from '../utils/hardReload';

const CHUNK_RELOAD_KEY = 'aurax_chunk_hard_reload';

function isChunkLoadError(error) {
  const msg = String(error?.message || error || '');
  return (
    error?.name === 'ChunkLoadError'
    || /Loading chunk \d+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
    || /Loading CSS chunk \d+ failed/i.test(msg) // REVENDAS_LOOP_0311
  );
}

function isRedirectLoopError(error) {
  const msg = String(error?.message || error || '');
  return /Maximum update depth exceeded/i.test(msg) || /Too many re-renders/i.test(msg);
}

/**
 * Evita tela branca quando um chunk lazy falha (cache antigo pós-deploy, rede, etc.).
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, autoReloadTried: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error?.message || error, error?.stack);

    if (!isChunkLoadError(error)) return;
    try {
      const already = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
      if (already) {
        this.setState({ autoReloadTried: true });
        return;
      }
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      this.setState({ autoReloadTried: true });
      // REVENDAS_LOOP_0311: chunk pode estar no cache HTTP como HTML (immutable). Rebusca sem cache antes do reload.
      const req = error && error.request;
      Promise.resolve(req ? fetch(req, { cache: 'reload' }).catch(() => null) : null)
        .finally(() => { void hardReloadApp(); });
    } catch {
      this.setState({ autoReloadTried: true });
    }
  }

  componentDidUpdate(_prevProps, prevState) {
    // Após hard reload bem-sucedido, limpa o flag para futuros deploys.
    if (prevState.error && !this.state.error) {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        /* ignore */
      }
    }
  }

  render() {
    const { error, autoReloadTried } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    const redirectLoop = isRedirectLoopError(error);
    const detail = String(error?.message || '').trim();

    if (chunk && !autoReloadTried) {
      // hardReload em curso — evita flash da mensagem.
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-6 py-12 text-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
            aria-label="A actualizar"
          />
          <p className="text-sm text-muted-foreground">A aplicar a versão mais recente…</p>
        </div>
      );
    }

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
        <div className="max-w-md space-y-2">
          <h1 className="text-lg font-semibold text-foreground">
            {chunk
              ? 'Atualização do Office pendente'
              : redirectLoop
                ? 'Conflito de permissões na navegação'
                : 'Não foi possível abrir esta página'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {chunk
              ? 'O navegador ainda usa arquivos antigos. Use «Limpar cache e recarregar» (ou Ctrl+F5) para aplicar a versão mais recente.'
              : redirectLoop
                ? 'A rota pedida não combina com os módulos liberados para sua conta. Recarregue a página; se persistir, peça ao administrador para revisar as permissões.'
                : 'Ocorreu um erro inesperado ao renderizar esta rota.'}
          </p>
          {detail ? ( /* REVENDAS_LOOP_0311: mostra erro real tambem p/ chunk */
            <p className="break-all text-left text-[11px] text-muted-foreground/80">{detail}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            className="h-9"
            onClick={() => {
              if (chunk || redirectLoop) {
                void hardReloadApp();
                return;
              }
              this.setState({ error: null });
            }}
          >
            {chunk || redirectLoop ? 'Recarregar agora' : 'Tentar novamente'}
          </Button>
          {chunk ? (
            <Button
              type="button"
              variant="outline"
              className="h-9"
              onClick={() => {
                try {
                  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                } catch {
                  /* ignore */
                }
                void hardReloadApp();
              }}
            >
              Limpar cache e recarregar
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
}
